import { ArrowLeftIcon, CheckIcon, PencilSquareIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { LayoutLoading } from 'components/LayoutLoading'
import { RichTextField } from 'components/RichTextEditor/RichTextField'
import { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  deleteTicketAttachment,
  deleteTicketComment,
  getConfig,
  getTicketDetails,
  sendTicketMessage,
  updateTicketComment,
  updateTicketDescription,
} from 'services/apis/ticket'
import { Button, DocumentFile, InputFileTable, Tooltip } from 'stories/components'
import { confirm, formatTime } from 'utils'
import { useTitle } from 'utils/pageTitle'

import { commentDisplayHtml, descriptionHtmlFromDetails, stripSubmittedByAttribution } from './ticketHtml'
import { TicketAttachmentChip } from './TicketImagePreview'
import { type IAsanaTicketItem, IAsanaAttachment, ITicketDetailsItem } from './types'

export const ExternalTicketItem = () => {
  const navigate = useHistory()

  const { id: ticketId } = useParams<{ id: string }>()

  const auth = useSelector((state: any) => state.auth)

  const [documentFiles, setDocumentFiles] = useState<DocumentFile[]>([])
  const [isLoading, setLoading] = useState(false)
  const [messages, setMessages] = useState<IAsanaTicketItem[]>([])
  const [attachments, setAttachments] = useState<IAsanaAttachment[]>([])
  const [msgText, setMsgText] = useState<string>('')
  const [details, setDetails] = useState<ITicketDetailsItem>()
  const [customKeys, setCustomKeys] = useState<string[]>([])
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, string>>({})

  const [editingDescription, setEditingDescription] = useState(false)
  const [descriptionDraft, setDescriptionDraft] = useState('')
  const [editingCommentGid, setEditingCommentGid] = useState<string | null>(null)
  const [commentDraft, setCommentDraft] = useState('')

  useTitle(details?.name || 'Ticket Details')

  useEffect(() => {
    if (isLoading || !ticketId) return

    getMsgs(ticketId)
  }, [ticketId])

  useEffect(() => {
    getConfig().then((data: any) => {
      const tempKeys = Object.keys(data || {})
      setCustomKeys(tempKeys)
    })
  }, [])

  useEffect(() => {
    if (!details || customKeys.length === 0) return
    const customValues = {} as Record<string, string>
    customKeys.forEach((fieldKey) => {
      const customField = details?.custom_fields?.find((field: any) => field.name === fieldKey)
      customValues[fieldKey] = customField?.display_value || ''
    })
    setCustomFieldValues(customValues)
  }, [customKeys, details])

  const applyDetails = (payload: {
    stories?: IAsanaTicketItem[]
    attachments?: IAsanaAttachment[]
    ticketDetails?: ITicketDetailsItem
  }) => {
    const filteredStories = (payload.stories || []).filter((story: any) =>
      ['attachment_added', 'comment_added'].includes(story.resource_subtype),
    )
    setMessages(filteredStories)
    setAttachments((payload.attachments || []).filter(Boolean))
    setDetails(payload.ticketDetails)
    setDescriptionDraft(descriptionHtmlFromDetails(payload.ticketDetails))
  }

  const getMsgs = (id: string) => {
    setLoading(true)
    return getTicketDetails(id)
      .then(({ stories, attachments: atts, ticketDetails }) => {
        applyDetails({ stories, attachments: atts, ticketDetails })
      })
      .finally(() => setLoading(false))
  }

  const refreshOnExpiredImage = useCallback(() => {
    if (!ticketId) return
    getTicketDetails(ticketId).then(({ stories, attachments: atts, ticketDetails }) => {
      applyDetails({ stories, attachments: atts, ticketDetails })
    })
  }, [ticketId])

  const sendTicketMsg = async () => {
    const plain = msgText.replace(/<[^>]+>/g, '').trim()
    if (!plain) {
      toast('Message cannot be empty', { type: 'error' })
      return
    }

    const msg: Record<string, any> = {
      ticketId: ticketId,
      text: msgText,
      createdBy: auth.profile.name ?? 'User',
      email: auth.profile.email,
      phone: auth.profile.phone,
      files: documentFiles,
    }

    setLoading(true)
    sendTicketMessage(msg)
      .then(() => {
        getMsgs(ticketId)
        setMsgText('')
        setDocumentFiles([])
      })
      .finally(() => setLoading(false))
  }

  const goBackToTickets = () => {
    navigate.push('/tickets')
  }

  const handleFilesChange = (files: DocumentFile[]) => {
    setDocumentFiles(files)
  }

  const startEditDescription = () => {
    setDescriptionDraft(descriptionHtmlFromDetails(details))
    setEditingDescription(true)
  }

  const cancelEditDescription = () => {
    setDescriptionDraft(descriptionHtmlFromDetails(details))
    setEditingDescription(false)
  }

  const saveDescription = async () => {
    if (!details?.gid) return
    setLoading(true)
    try {
      const result = await updateTicketDescription(details.gid, descriptionDraft || '')
      if (result?.success === false) {
        toast(result.message || 'Failed to update description', { type: 'error' })
        return
      }
      if (result?.ticketDetails) {
        applyDetails(result)
      } else {
        await getMsgs(details.gid)
      }
      setEditingDescription(false)
      toast('Description updated', { type: 'success' })
    } catch (err: any) {
      toast(err?.message || 'Failed to update description', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const startEditComment = (msg: IAsanaTicketItem) => {
    setEditingCommentGid(msg.gid)
    setCommentDraft(stripSubmittedByAttribution(msg.html_text || msg.text || ''))
  }

  const cancelEditComment = () => {
    setEditingCommentGid(null)
    setCommentDraft('')
  }

  const saveComment = async (msg: IAsanaTicketItem) => {
    const plain = commentDraft.replace(/<[^>]+>/g, '').trim()
    if (!plain) {
      toast('Comment cannot be empty', { type: 'error' })
      return
    }
    setLoading(true)
    try {
      const createdBy = auth.profile.name ?? 'User'
      const result = await updateTicketComment(msg.gid, commentDraft, createdBy)
      if (result?.success === false) {
        toast(result.message || 'Failed to update comment', { type: 'error' })
        return
      }
      setEditingCommentGid(null)
      setCommentDraft('')
      await getMsgs(ticketId)
      toast('Comment updated', { type: 'success' })
    } catch (err: any) {
      toast(err?.message || 'Failed to update comment', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const removeComment = async (msg: IAsanaTicketItem) => {
    const ok = await confirm('Are you sure you want to delete this comment?')
    if (!ok) return
    setLoading(true)
    try {
      const result = await deleteTicketComment(msg.gid)
      if (result?.success === false) {
        toast(result.message || 'Failed to delete comment', { type: 'error' })
        return
      }
      if (editingCommentGid === msg.gid) {
        setEditingCommentGid(null)
        setCommentDraft('')
      }
      await getMsgs(ticketId)
      toast('Comment deleted', { type: 'success' })
    } catch (err: any) {
      toast(err?.message || 'Failed to delete comment', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const removeAttachment = async (attachmentGid: string, name: string) => {
    const ok = await confirm(`Are you sure you want to delete "${name}"?`)
    if (!ok) return
    setLoading(true)
    try {
      const result = await deleteTicketAttachment(attachmentGid)
      if (result?.success === false) {
        toast(result.message || 'Failed to delete attachment', { type: 'error' })
        return
      }
      await getMsgs(ticketId)
      toast('Attachment deleted', { type: 'success' })
    } catch (err: any) {
      toast(err?.message || 'Failed to delete attachment', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const descriptionHtml = descriptionHtmlFromDetails(details)

  return (
    <div className="py-6 px-2">
      <div className="relative shadow1 max-w-screen-2xl m-auto bg-white rounded p-3 md:p-7 pb-3 md:pb-3 sm:text-center lg:text-left w-full mb-6">
        <LayoutLoading show={isLoading} />

        <div className="flex flex-wrap items-center gap-2 mb-3 justify-between">
          <h1
            className="text-2xl font-variation-settings-600 flex gap-1 items-center cursor-pointer text-shade-blue hover:underline"
            onClick={goBackToTickets}
          >
            <ArrowLeftIcon className="w-3 h-3" />
            <span className="ml-2 uppercase text-sm">Back</span>
          </h1>
        </div>
        <div className="py-6 border-b">
          <h2 className="text-xl font-semibold mb-4 text-gray-800">Ticket Details</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm font-semibold text-gray-600 mb-1">Subject:</p>
              <p className="text-base text-gray-900">{details?.name}</p>
            </div>

            {customFieldValues &&
              Object.entries(customFieldValues as Record<string, string>).map(([key, value]) => (
                <div key={key}>
                  <p className="text-sm font-semibold text-gray-600 mb-1">{key}:</p>
                  <p className="text-base text-gray-900">{value || '-'}</p>
                </div>
              ))}
          </div>

          <div className="mt-4">
            <div className="flex items-center justify-between gap-2 mb-1">
              <p className="text-sm font-semibold text-gray-600">Description:</p>
              {!editingDescription ? (
                <Tooltip message="Edit description">
                  <button
                    type="button"
                    onClick={startEditDescription}
                    className="inline-flex items-center justify-center p-1.5 rounded-md text-gray-500 hover:text-shade-blue hover:bg-sky-50 border border-transparent hover:border-sky-100 transition-colors"
                    aria-label="Edit description"
                  >
                    <PencilSquareIcon className="w-4 h-4" />
                  </button>
                </Tooltip>
              ) : (
                <div className="flex items-center gap-1">
                  <Tooltip message="Save">
                    <button
                      type="button"
                      onClick={saveDescription}
                      className="inline-flex items-center justify-center p-1.5 rounded-md text-emerald-700 hover:bg-emerald-50 border border-emerald-100"
                      aria-label="Save description"
                    >
                      <CheckIcon className="w-4 h-4" />
                    </button>
                  </Tooltip>
                  <Tooltip message="Cancel">
                    <button
                      type="button"
                      onClick={cancelEditDescription}
                      className="inline-flex items-center justify-center p-1.5 rounded-md text-gray-500 hover:bg-gray-100 border border-gray-200"
                      aria-label="Cancel edit"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </Tooltip>
                </div>
              )}
            </div>
            {editingDescription ? (
              <div className="rounded-lg border border-gray-200 overflow-hidden mt-2 text-left">
                <RichTextField value={descriptionDraft || ''} onChange={setDescriptionDraft} minHeight={180} />
              </div>
            ) : descriptionHtml ? (
              <div
                className="text-xs ck-content !min-h-fit mt-4 whitespace-pre-wrap text-left"
                dangerouslySetInnerHTML={{ __html: descriptionHtml }}
              />
            ) : (
              <p className="text-sm text-gray-500 italic mt-4">No description</p>
            )}
          </div>

          {attachments.length > 0 && (
            <div className="mt-4">
              <p className="text-sm font-semibold text-gray-600 mb-2">Attachments:</p>
              <div className="flex flex-wrap gap-2">
                {attachments.map((attachment) => (
                  <TicketAttachmentChip
                    key={attachment.gid}
                    name={attachment.name}
                    url={attachment.download_url}
                    onExpiredUrl={refreshOnExpiredImage}
                    onDelete={() => removeAttachment(attachment.gid, attachment.name)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {messages.slice().map((msg) => {
          const isComment = msg.resource_subtype === 'comment_added'
          const isAttachmentStory = msg.resource_subtype === 'attachment_added'
          const isEditing = editingCommentGid === msg.gid
          const displayHtml = commentDisplayHtml(msg)
          const attachmentGid = msg.attachment_gid

          return (
            <div key={msg.gid}>
              <div className="py-4 border-b px-8 border-l-2 border-l-gray-400 bg-gray-100">
                <div className="flex justify-between items-center gap-2">
                  <p className="font-semibold">{msg.created_by?.name}</p>
                  <div className="flex items-center gap-2">
                    <p className="font-light text-gray-500 text-sm">{formatTime(msg.created_at)}</p>
                    {isComment && !isEditing && (
                      <>
                        <Tooltip message="Edit comment">
                          <button
                            type="button"
                            onClick={() => startEditComment(msg)}
                            className="inline-flex items-center justify-center p-1 rounded-md text-gray-500 hover:text-shade-blue hover:bg-white/80"
                            aria-label="Edit comment"
                          >
                            <PencilSquareIcon className="w-4 h-4" />
                          </button>
                        </Tooltip>
                        <Tooltip message="Delete comment">
                          <button
                            type="button"
                            onClick={() => removeComment(msg)}
                            className="inline-flex items-center justify-center p-1 rounded-md text-gray-500 hover:text-red-600 hover:bg-white/80"
                            aria-label="Delete comment"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </Tooltip>
                      </>
                    )}
                    {isAttachmentStory && attachmentGid && msg.file_name && !isEditing && (
                      <Tooltip message="Delete attachment">
                        <button
                          type="button"
                          onClick={() => removeAttachment(attachmentGid, msg.file_name || 'attachment')}
                          className="inline-flex items-center justify-center p-1 rounded-md text-gray-500 hover:text-red-600 hover:bg-white/80"
                          aria-label="Delete attachment"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </Tooltip>
                    )}
                    {isComment && isEditing && (
                      <div className="flex items-center gap-1">
                        <Tooltip message="Save">
                          <button
                            type="button"
                            onClick={() => saveComment(msg)}
                            className="inline-flex items-center justify-center p-1 rounded-md text-emerald-700 hover:bg-emerald-50"
                            aria-label="Save comment"
                          >
                            <CheckIcon className="w-4 h-4" />
                          </button>
                        </Tooltip>
                        <Tooltip message="Cancel">
                          <button
                            type="button"
                            onClick={cancelEditComment}
                            className="inline-flex items-center justify-center p-1 rounded-md text-gray-500 hover:bg-white/80"
                            aria-label="Cancel edit"
                          >
                            <XMarkIcon className="w-4 h-4" />
                          </button>
                        </Tooltip>
                      </div>
                    )}
                  </div>
                </div>
                {isEditing ? (
                  <div className="mt-4 rounded-lg border border-gray-200 overflow-hidden bg-white text-left">
                    <RichTextField value={commentDraft} onChange={setCommentDraft} minHeight={120} />
                  </div>
                ) : displayHtml ? (
                  <div
                    className="text-xs ck-content !min-h-fit mt-4 whitespace-pre-wrap text-left"
                    dangerouslySetInnerHTML={{ __html: displayHtml }}
                  />
                ) : null}
                {msg?.downloaded_url && msg?.file_name && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    <TicketAttachmentChip
                      name={msg.file_name}
                      url={msg.downloaded_url}
                      onExpiredUrl={refreshOnExpiredImage}
                      onDelete={
                        attachmentGid
                          ? () => removeAttachment(attachmentGid, msg.file_name || 'attachment')
                          : undefined
                      }
                    />
                  </div>
                )}
              </div>
            </div>
          )
        })}
        <div className="w-full border-t-2 border-t-gray-200">
          <p className="my-4 text-sm text-gray-900">Message Content</p>
          <div className="text-left">
            <RichTextField value={msgText} onChange={setMsgText} title="Reply" minHeight={140} required />
            <div className="mt-2">
              <InputFileTable
                value={documentFiles}
                onChange={handleFilesChange}
                title="Files"
                acceptFileTypes="application/*,image/*"
                filePath={`nexys/ticket/${ticketId}`}
                showStatus={false}
                showCategory={false}
              />
            </div>
          </div>
          <div className="flex items-center gap-2 mt-4 pb-6">
            <Button onClick={sendTicketMsg} className="h-fit" size="sm">
              Submit
            </Button>
            <Button
              onClick={() => {
                goBackToTickets()
              }}
              className="h-fit"
              outline
              size="sm"
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
